import { useEffect, useRef } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { getAccessToken } from './api';

/**
 * Subscribes to realtime updates for a single order. The callback fires whenever
 * the server broadcasts an "OrderUpdated" event (a condition report or status change),
 * so both phones at the handover meeting stay in sync.
 */
export function useOrderHub(orderId: string | undefined, onUpdate: () => void) {
  const callbackRef = useRef(onUpdate);
  callbackRef.current = onUpdate;

  useEffect(() => {
    if (!orderId) return;

    const numericId = Number(orderId);
    if (Number.isNaN(numericId)) return;

    let cancelled = false;
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl('/hubs/orders', {
        accessTokenFactory: () => getAccessToken() ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on('OrderUpdated', () => {
      callbackRef.current();
    });

    // Re-join the order group after an automatic reconnect.
    connection.onreconnected(() => {
      void connection.invoke('SubscribeToOrder', numericId).catch(() => {});
    });

    void connection
      .start()
      .then(() => {
        if (cancelled) return;
        return connection.invoke('SubscribeToOrder', numericId);
      })
      .catch(() => {
        // Realtime is best-effort; the page still works via manual fetches.
      });

    return () => {
      cancelled = true;
      void connection.stop();
    };
  }, [orderId]);
}
